import { STORE_BASE } from "./paths";
import { ForegroundPushPayload, VendorPushMessage, parseForegroundPush, sameOriginPushPath } from "./webPushContent";
import { fingerprintWebPushToken, recordWebPushDiagnostic } from "./webPushDiagnostics";
import { history } from "../shared/_helpers/history";

const STORAGE_KEY = "mami-vendor-web-push";
const OPEN_QUEUE_KEY = `${STORAGE_KEY}:pending-opens`;
const OPENED_KEY = `${STORAGE_KEY}:tracked-opens`;
const OPEN_QUERY_KEY = "pushOpen";
const OPEN_MESSAGE_TYPE = "MAMI_WEB_PUSH_OPEN";
const NOTIFICATION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SYNC_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;
const TOKEN_CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || undefined,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || undefined,
};
const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";

type PushAccount = { id: string; accessToken: string };
type Registration = { accountId: string; token: string; syncedAt: number; enabled: boolean };
type PendingOpen = { accountId: string; notificationId: string };
export type WebPushState = {
  status: "unavailable" | "off" | "working" | "enabled" | "denied" | "error";
  accountId: string | null;
  dismissed: boolean;
  message: string;
  notification: VendorPushMessage | null;
};

type FirebaseMessaging = {
  getToken: (options: { vapidKey: string; serviceWorkerRegistration: ServiceWorkerRegistration }) => Promise<string>;
  deleteToken: () => Promise<boolean>;
  onMessage: (callback: (payload: any) => void) => () => void;
};
const configured = Boolean(vapidKey && firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId && firebaseConfig.messagingSenderId && firebaseConfig.appId);
let account: PushAccount | null = null;
let state: WebPushState = {
  status: configured ? "off" : "unavailable", accountId: null, dismissed: false, message: "", notification: null,
};
const serverState: WebPushState = { status: "unavailable", accountId: null, dismissed: false, message: "", notification: null };
const listeners = new Set<() => void>();
let messagingPromise: Promise<FirebaseMessaging> | null = null;
let unsubscribeForeground: (() => void) | null = null;
let inFlight: Promise<void> | null = null;
let lastTokenCheckAt = 0;
let pendingReset: Promise<void> | null = null;
let pendingOpenSync: Promise<void> | null = null;
let openSyncRequested = false;
const recentForegroundMessages = new Set<string>();

const emit = (patch: Partial<WebPushState>) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};
export const subscribeWebPush = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
export const getWebPushState = () => state;
export const getServerWebPushState = () => serverState;

const readRegistration = (): Registration | null => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    return value && typeof value.accountId === "string" ? value : null;
  } catch { return null; }
};
const saveRegistration = (registration: Registration | null) => {
  if (registration) localStorage.setItem(STORAGE_KEY, JSON.stringify(registration));
  else localStorage.removeItem(STORAGE_KEY);
};
const dismissedKey = (id: string) => `${STORAGE_KEY}:dismissed:${id}`;

const readPendingOpens = (): PendingOpen[] => {
  try {
    const value = JSON.parse(localStorage.getItem(OPEN_QUEUE_KEY) || "[]");
    return Array.isArray(value) ? value.filter((item): item is PendingOpen =>
      typeof item?.accountId === "string" && typeof item?.notificationId === "string" &&
      NOTIFICATION_ID_PATTERN.test(item.notificationId)) : [];
  } catch { return []; }
};
const savePendingOpens = (items: PendingOpen[]) => {
  if (items.length) localStorage.setItem(OPEN_QUEUE_KEY, JSON.stringify(items));
  else localStorage.removeItem(OPEN_QUEUE_KEY);
};
const readTrackedOpens = (): PendingOpen[] => {
  try {
    const value = JSON.parse(localStorage.getItem(OPENED_KEY) || "[]");
    return Array.isArray(value) ? value.filter((item): item is PendingOpen =>
      typeof item?.accountId === "string" && typeof item?.notificationId === "string" &&
      NOTIFICATION_ID_PATTERN.test(item.notificationId)) : [];
  } catch { return []; }
};

const notificationApiBase = () => {
  return window.location.origin === "https://vendor.mamimarkets.com"
    ? "https://backofficeapi.mamimarkets.com"
    : "https://staging-backofficeapi.mamimarkets.com";
};

const flushPendingOpens = () => {
  if (pendingOpenSync) {
    openSyncRequested = true;
    return pendingOpenSync;
  }
  pendingOpenSync = (async () => {
    const current = account;
    const registration = readRegistration();
    const base = notificationApiBase();
    if (!current || !registration?.enabled || registration.accountId !== current.id || !base) return;
    for (const item of readPendingOpens().filter((entry) => entry.accountId === current.id)) {
      try {
        const response = await fetch(`${base}/api/v1/Notifications/${encodeURIComponent(item.notificationId)}/track-open`, {
          method: "POST",
          headers: { Authorization: `Bearer ${current.accessToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ deviceToken: registration.token }),
        });
        const result = await response.json().catch(() => null);
        if (!response.ok || result?.success !== true) return;
        if (account?.id !== current.id) return;
        const tracked = readTrackedOpens();
        if (!tracked.some((entry) => entry.accountId === item.accountId && entry.notificationId === item.notificationId)) {
          localStorage.setItem(OPENED_KEY, JSON.stringify([...tracked, item].slice(-100)));
        }
        savePendingOpens(readPendingOpens().filter((entry) =>
          entry.accountId !== item.accountId || entry.notificationId !== item.notificationId));
      } catch { return; }
    }
  })().finally(() => {
    pendingOpenSync = null;
    if (openSyncRequested) {
      openSyncRequested = false;
      void flushPendingOpens();
    }
  });
  return pendingOpenSync;
};

export const queueWebPushOpen = (notificationId: string | null | undefined) => {
  if (!notificationId || !NOTIFICATION_ID_PATTERN.test(notificationId)) return;
  const registration = readRegistration();
  if (!registration?.enabled) return;
  if (readTrackedOpens().some((item) => item.accountId === registration.accountId && item.notificationId === notificationId)) return;
  const pending = readPendingOpens();
  if (!pending.some((item) => item.accountId === registration.accountId && item.notificationId === notificationId)) {
    savePendingOpens([...pending, { accountId: registration.accountId, notificationId }]);
  }
  void flushPendingOpens();
};

export const showForegroundPush = (
  payload: ForegroundPushPayload | null | undefined,
  source: VendorPushMessage["source"] = "fcm",
) => {
  if (source === "synthetic" && process.env.NODE_ENV !== "development") return;
  if (source === "fcm") {
    recordWebPushDiagnostic("foreground_received", {
      notificationId: payload?.data?.notificationId || null,
      visibility: document.visibilityState,
      focused: document.hasFocus(),
      detail: payload?.notification ? "notification_payload" : "data_payload",
    });
  } else {
    recordWebPushDiagnostic("synthetic_foreground_triggered");
  }
  const message = parseForegroundPush(payload, window.location.origin, source);
  if (!message) {
    recordWebPushDiagnostic("foreground_payload_processed", { outcome: "invalid", detail: source });
    return;
  }
  const dedupeKey = source === "fcm" ? message.notificationId || payload?.messageId : null;
  if (dedupeKey && recentForegroundMessages.has(dedupeKey)) {
    recordWebPushDiagnostic("foreground_payload_processed", {
      notificationId: message.notificationId, outcome: "duplicate", detail: source,
    });
    return;
  }
  if (dedupeKey) {
    recentForegroundMessages.add(dedupeKey);
    if (recentForegroundMessages.size > 100) recentForegroundMessages.delete(recentForegroundMessages.values().next().value!);
  }
  recordWebPushDiagnostic("foreground_payload_processed", {
    notificationId: message.notificationId, outcome: "accepted", detail: source,
  });
  emit({ notification: message });
  recordWebPushDiagnostic("foreground_state_updated", { notificationId: message.notificationId, detail: source });
};

export const listenForWebPushOpens = () => {
  if (!("serviceWorker" in navigator)) return () => undefined;
  const onMessage = (event: MessageEvent) => {
    if (event.data?.type === "MAMI_WEB_PUSH_DIAGNOSTIC") {
      recordWebPushDiagnostic(event.data.stage, event.data.details);
      return;
    }
    if (event.data?.type !== OPEN_MESSAGE_TYPE) return;
    recordWebPushDiagnostic("background_notification_opened", {
      notificationId: event.data.notificationId || null,
    });
    queueWebPushOpen(event.data.notificationId);
    history.push(sameOriginPushPath(event.data.deepLink, window.location.origin));
  };
  navigator.serviceWorker.addEventListener("message", onMessage);
  const url = new URL(window.location.href);
  const notificationId = url.searchParams.get(OPEN_QUERY_KEY);
  if (notificationId) {
    queueWebPushOpen(notificationId);
    url.searchParams.delete(OPEN_QUERY_KEY);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }
  return () => navigator.serviceWorker.removeEventListener("message", onMessage);
};

const getMessagingClient = () => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      recordWebPushDiagnostic("firebase_initializing", { projectId: firebaseConfig.projectId });
      if (!configured || !window.isSecureContext || !("serviceWorker" in navigator) || !("Notification" in window)) {
        throw new Error("Browser notifications are unavailable here.");
      }
      const [firebaseApp, firebaseMessaging] = await Promise.all([import("firebase/app"), import("firebase/messaging")]);
      if (!(await firebaseMessaging.isSupported())) throw new Error("This browser does not support push notifications.");
      const app = firebaseApp.getApps().find((existing) => existing.name === "web-push") || firebaseApp.initializeApp(firebaseConfig, "web-push");
      const messaging = firebaseMessaging.getMessaging(app);
      recordWebPushDiagnostic("firebase_initialized", { projectId: firebaseConfig.projectId });
      const client: FirebaseMessaging = {
        getToken: (options) => firebaseMessaging.getToken(messaging, options),
        deleteToken: () => firebaseMessaging.deleteToken(messaging),
        onMessage: (callback) => firebaseMessaging.onMessage(messaging, callback),
      };
      if (!unsubscribeForeground) {
        unsubscribeForeground = client.onMessage((payload) => showForegroundPush(payload));
        recordWebPushDiagnostic("foreground_listener_attached");
      }
      return client;
    })().catch((error) => {
      recordWebPushDiagnostic("firebase_initialization_failed", {
        outcome: error instanceof Error ? error.name : "unknown",
      });
      messagingPromise = null;
      throw error;
    });
  }
  return messagingPromise;
};

const getToken = async () => {
  const messaging = await getMessagingClient();
  const workerUrl = new URL("/firebase-messaging-sw.js", window.location.origin);
  Object.entries(firebaseConfig).forEach(([key, value]) => {
    if (value) workerUrl.searchParams.set(key, value);
  });
  recordWebPushDiagnostic("worker_registering", { projectId: firebaseConfig.projectId });
  const worker = await navigator.serviceWorker.register(workerUrl.toString(), { scope: "/" });
  recordWebPushDiagnostic("worker_registered", {
    outcome: worker.active?.state || worker.installing?.state || worker.waiting?.state || "unknown",
    detail: navigator.serviceWorker.controller ? "page_controlled" : "page_uncontrolled",
  });
  const token = await messaging.getToken({ vapidKey, serviceWorkerRegistration: worker });
  if (!token) throw new Error("Firebase did not return a browser token.");
  try {
    const subscription = await worker.pushManager.getSubscription();
    recordWebPushDiagnostic("browser_subscription_checked", {
      outcome: subscription ? "present" : "absent",
    });
  } catch {
    recordWebPushDiagnostic("browser_subscription_checked", { outcome: "unavailable" });
  }
  recordWebPushDiagnostic("fcm_token_acquired", {
    tokenFingerprint: await fingerprintWebPushToken(token),
  });
  return token;
};

const tokenRequest = async (method: "POST" | "DELETE", token: string, accessToken: string) => {
  const base = STORE_BASE?.replace(/\/$/, "");
  if (!base) throw new Error("Product API is not configured.");
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 7000);
  let response: Response;
  const tokenFingerprint = await fingerprintWebPushToken(token);
  recordWebPushDiagnostic("device_token_request", { tokenFingerprint, detail: method });
  try {
    response = await fetch(`${base}/v1/DeviceTokens`, {
      method,
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(method === "POST" ? { token, platform: 3 } : { token }),
      signal: controller.signal,
    });
  } catch (error) {
    recordWebPushDiagnostic("device_token_network_failed", {
      tokenFingerprint, detail: method, outcome: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  } finally { clearTimeout(timeout); }
  const result = await response.json().catch(() => null);
  recordWebPushDiagnostic("device_token_response", {
    tokenFingerprint, detail: method,
    outcome: `${response.status}:${result?.success === true ? "success" : "failure"}`,
  });
  if (!response.ok || result?.success !== true) throw new Error(result?.message || "Token registration failed.");
};

const synchronize = async (force = false) => {
  if (!account || !configured || !("Notification" in window) || Notification.permission !== "granted") return;
  const current = account;
  const old = readRegistration();
  if (!old?.enabled || old.accountId !== current.id) return;
  recordWebPushDiagnostic("token_sync_started", { outcome: force ? "forced" : "scheduled" });
  try {
    const token = await getToken();
    lastTokenCheckAt = Date.now();
    recordWebPushDiagnostic("fcm_token_checked", {
      tokenFingerprint: await fingerprintWebPushToken(token),
      outcome: token === old.token ? "unchanged" : "rotated",
    });
    if (account?.id !== current.id) return;
    if (force || token !== old.token || Date.now() - old.syncedAt >= SYNC_INTERVAL_MS) {
      await tokenRequest("POST", token, current.accessToken);
      if (old.token && old.token !== token) void tokenRequest("DELETE", old.token, current.accessToken).catch(() => undefined);
      saveRegistration({ accountId: current.id, token, enabled: true, syncedAt: Date.now() });
    }
    emit({ status: "enabled", message: "" });
  } catch (error) {
    recordWebPushDiagnostic("token_sync_failed", { outcome: error instanceof Error ? error.name : "unknown" });
    emit({ status: "error", message: error instanceof Error ? error.message : "Could not register notifications." });
  }
};

export const setWebPushAccount = (next: PushAccount | null) => {
  if (next && account?.id === next.id && account.accessToken === next.accessToken && state.accountId === next.id) return;
  account = next;
  if (!next) { emit({ accountId: null, status: configured ? "off" : "unavailable", notification: null }); return; }
  const saved = readRegistration();
  if (saved && saved.accountId !== next.id) {
    saveRegistration(null);
    savePendingOpens(readPendingOpens().filter((item) => item.accountId !== saved.accountId));
    pendingReset = getMessagingClient().then((messaging) => messaging.deleteToken()).then(() => undefined).catch(() => undefined).finally(() => { pendingReset = null; });
  }
  const dismissed = localStorage.getItem(dismissedKey(next.id)) === "true";
  const supported = configured && window.isSecureContext && "serviceWorker" in navigator && "Notification" in window;
  const status = !supported ? "unavailable" : Notification.permission === "denied" ? "denied" : saved?.accountId === next.id && saved.enabled ? "enabled" : "off";
  recordWebPushDiagnostic("account_push_state", {
    outcome: status,
    detail: saved?.accountId === next.id ? "saved_registration_matches" : "no_matching_registration",
  });
  emit({ accountId: next.id, dismissed, status, message: "" });
  if (status === "denied" && saved?.accountId === next.id) {
    void tokenRequest("DELETE", saved.token, next.accessToken).catch(() => undefined);
    saveRegistration(null);
    void getMessagingClient().then((messaging) => messaging.deleteToken()).catch(() => undefined);
  }
  if (status === "enabled") void synchronize();
  void flushPendingOpens();
};

export const dismissWebPushInvitation = () => {
  if (!account) return;
  localStorage.setItem(dismissedKey(account.id), "true");
  emit({ dismissed: true });
};

export const enableWebPush = async () => {
  if (!account || inFlight) return inFlight;
  const current = account;
  inFlight = (async () => {
    emit({ status: "working", message: "" });
    try {
      if (!configured) throw new Error("Notifications are not configured yet.");
      if (!window.isSecureContext || !("Notification" in window) || !("serviceWorker" in navigator)) throw new Error("This browser does not support notifications.");
      recordWebPushDiagnostic("permission_requesting", { outcome: Notification.permission });
      const permission = await Notification.requestPermission();
      recordWebPushDiagnostic("permission_result", { outcome: permission });
      if (permission !== "granted") { emit({ status: "denied", message: "Allow notifications in your browser settings to enable them." }); return; }
      if (pendingReset) await pendingReset;
      const previous = readRegistration();
      if (previous?.accountId && previous.accountId !== current.id) {
        try { await (await getMessagingClient()).deleteToken(); } catch { /* Old browser token will expire. */ }
      }
      const token = await getToken();
      if (account?.id !== current.id) return;
      await tokenRequest("POST", token, current.accessToken);
      if (previous?.accountId === current.id && previous.token !== token) {
        void tokenRequest("DELETE", previous.token, current.accessToken).catch(() => undefined);
      }
      saveRegistration({ accountId: current.id, token, enabled: true, syncedAt: Date.now() });
      emit({ status: "enabled", message: "" });
      void flushPendingOpens();
    } catch (error) {
      recordWebPushDiagnostic("enable_failed", { outcome: error instanceof Error ? error.name : "unknown" });
      emit({ status: "error", message: error instanceof Error ? error.message : "Could not enable notifications." });
    }
  })().finally(() => { inFlight = null; });
  return inFlight;
};

export const disableWebPush = async () => {
  const saved = readRegistration();
  let serverRemoved = true;
  let browserRemoved = true;
  if (saved?.token && account?.id === saved.accountId) {
    try { await tokenRequest("DELETE", saved.token, account.accessToken); } catch { serverRemoved = false; }
  }
  try { await (await getMessagingClient()).deleteToken(); } catch { browserRemoved = false; }
  if (!serverRemoved && !browserRemoved) {
    emit({ status: "enabled", dismissed: true, message: "Could not disable notifications. Retry or block this site in browser settings." });
    return;
  }
  saveRegistration(null);
  if (saved) savePendingOpens(readPendingOpens().filter((item) => item.accountId !== saved.accountId));
  if (account) localStorage.setItem(dismissedKey(account.id), "true");
  emit({ status: configured ? "off" : "unavailable", dismissed: true, message: !browserRemoved ? "Server registration removed, but browser cleanup could not be confirmed." : !serverRemoved ? "Browser unsubscribed; server removal could not be confirmed." : "" });
};

export const revokeWebPushForLogout = async () => {
  const saved = readRegistration();
  if (!saved || !account || saved.accountId !== account.id) return;
  await disableWebPush();
};

export const clearWebPushMessage = () => emit({ notification: null });
export const openForegroundPush = (notification: VendorPushMessage) => {
  recordWebPushDiagnostic("foreground_card_clicked", {
    notificationId: notification.notificationId,
    detail: notification.source,
  });
  if (notification.source === "fcm") {
    if (notification.notificationId) queueWebPushOpen(notification.notificationId);
    else recordWebPushDiagnostic("open_tracking_unavailable", { outcome: "missing_notification_id" });
  }
  clearWebPushMessage();
  history.push(sameOriginPushPath(notification.deepLink, window.location.origin));
};
export const refreshWebPush = () => {
  if (Date.now() - lastTokenCheckAt >= TOKEN_CHECK_INTERVAL_MS) void synchronize();
  void flushPendingOpens();
};
